import { motion } from 'framer-motion';
import { Download, FileText, Briefcase, GraduationCap, Award, Globe, Hammer, Monitor, Camera } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';

export const ResumeWindow = () => {
  const handleDownload = () => {
    // This looks for 'Alvin.pdf' in your public folder
    const link = document.createElement('a');
    link.href = '/Alvin.pdf'; 
    link.download = 'Alvin Jo Regi - Resume.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Resume download started!');
  };

  const experience = [
    {
      title: 'Founder & CEO',
      company: 'Pinnacle Event Organizing',
      period: 'July 2024 - Present',
      achievements: [
        'Lead youth-driven large-scale events, coordinating logistics, technical operations, and creative marketing.',
        'Manage end-to-end execution of events focused on community engagement and financial literacy.'
      ]
    },
    {
      title: 'TEDxSISJ Youth Head of Event Operations',
      company: 'TED Conferences',
      period: 'April 2025 - Present',
      achievements: [
        'Oversee all technical aspects including sound, lighting, stage design, and visual systems.',
        'Coordinate with production crews for seamless execution and real-time troubleshooting.',
        'Maintain alignment with TEDx standards to deliver professional, inspiring speaker impacts.'
      ]
    },
    {
      title: 'Technical Director - Student Council',
      company: 'Sharjah Indian School, Juwaiza',
      period: 'April 2023 - Present',
      achievements: [
        'Lead technical planning for all major school events, assemblies, and cultural festivals.',
        'Manage live sound, lighting, and visual systems using Yamaha, Allen & Heath, and Midas consoles.',
        'Mentor and train fellow students in handling professional A/V equipment and DMX-512 lighting systems.'
      ]
    },
    {
      title: 'Radio Club President',
      company: 'Sharjah Indian School, Juwaiza',
      period: 'April 2024 - Present',
      achievements: [
        'Transformed the club into a vibrant media platform with live student-led broadcasts.',
        'Oversee content creation, scripting, audio quality, and member mentoring.'
      ]
    },
    {
      title: 'Technical Lead for SIS FEST\'24',
      company: 'Sharjah Indian School, Juwaiza',
      period: 'October 2024 - December 2024',
      achievements: [
        'Responsible for overseeing all technical aspects of the school\'s biggest cultural festival.',
        'Managed stage setup, sound, lighting, and digital media coordination for 1000+ attendees.'
      ]
    },
    {
      title: 'Social Awareness Club Joint Secretary',
      company: 'Sharjah Indian School, Juwaiza',
      period: 'April 2024 - May 2025',
      achievements: [
        'Organized awareness campaigns and outreach programs to inspire student civic involvement.',
        'Mentored club members and coordinated events promoting empathy and leadership.'
      ]
    },
    {
      title: 'Technical Support (Freelance)',
      company: 'St. Michael\'s Church / AMS Event Productions',
      period: '2023 - Present',
      achievements: [
        'Provide expert live audio-visual support, camera shifts, and screen management for large gatherings.',
        'Collaborate with Indian Association Sharjah for technical leadership in community events.'
      ]
    }
  ];

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-start justify-between"
      >
        <div>
          <h1 className="text-4xl font-bold glow-text mb-2 uppercase">Alvin Jo Regi</h1>
          <p className="text-muted-foreground">Technical Director | Founder of Pinnacle | Full Stack Developer</p>
        </div>
        <Button onClick={handleDownload} className="gap-2">
          <Download className="w-4 h-4" />
          Download PDF
        </Button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="p-8 tech-border">
          {/* Header Section */}
          <div className="text-center mb-8 pb-8 border-b border-border">
            <h2 className="text-2xl font-bold text-accent mb-4">Professional Profile</h2>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground mb-4">
              <span>alvinjoregi76@gmail.com</span>
              <span>•</span>
              <span>+971 55 682 2374</span>
              <span>•</span>
              <span>Sharjah, Abushargara</span>
              <span>•</span>
              <span>linkedin.com/in/alvinjoregi</span>
            </div>
            <p className="max-w-3xl mx-auto text-foreground/80 leading-relaxed italic">
              "I enjoy working behind the scenes to bring ideas to life, blending creativity with technical precision to create seamless event experiences."
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column: Experience */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <div className="flex items-center gap-2 mb-6">
                  <Briefcase className="w-5 h-5 text-primary" />
                  <h3 className="text-xl font-bold text-primary">Experience</h3>
                </div>
                <div className="space-y-6">
                  {experience.map((job, i) => (
                    <div key={i} className="border-l-2 border-primary/30 pl-4">
                      <div className="font-bold text-foreground">{job.title}</div>
                      <div className="text-sm text-accent mb-1">{job.company}</div>
                      <div className="text-xs text-muted-foreground mb-2">{job.period}</div>
                      <ul className="list-disc list-inside space-y-1">
                        {job.achievements.map((achievement, j) => (
                          <li key={j} className="text-sm text-foreground/70">{achievement}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-4">
                  <GraduationCap className="w-5 h-5 text-primary" />
                  <h3 className="text-xl font-bold text-primary">Education</h3>
                </div>
                <div className="pl-4 space-y-4">
                  <div>
                    <div className="font-bold text-foreground">Sharjah Indian School, Juwaiza</div>
                    <div className="text-sm text-muted-foreground">High School Diploma, Commerce-Sociology</div>
                    <div className="text-xs text-muted-foreground">2016 - Present (Expected Graduation 2026)</div>
                  </div>
                  <div>
                    <div className="font-bold text-foreground">Pace Indian International</div>
                    <div className="text-sm text-muted-foreground">Primary Education (KG - Grade 3)</div>
                    <div className="text-xs text-muted-foreground">2011 - 2016</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Skills & Info */}
            <div className="space-y-8">
              <Card className="p-4 bg-primary/5 border-primary/20">
                <div className="flex items-center gap-2 mb-4">
                  <Hammer className="w-5 h-5 text-accent" />
                  <h3 className="text-lg font-bold text-accent">Technical Arsenal</h3>
                </div>
                <div className="text-sm space-y-2 text-foreground/80">
                  <p><strong>Audio Consoles:</strong> Yamaha (Digital), Allen & Heath, Midas</p>
                  <p><strong>Light Control:</strong> Tiger Touch, DMX-512 Systems</p>
                  <p><strong>Media:</strong> Photography, Videography, Live Broadcasting</p>
                  <p><strong>Code:</strong> JS, Python, Node.js, DJS, React</p>
                </div>
              </Card>

              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Award className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-bold text-primary">Core Competencies</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    'Live Sound Engineering', 'Live Light Engineering', 
                    'Event Management', 'Technical Leadership', 
                    'Public Speaking', 'Camera Operations', 'Full Stack Dev'
                  ].map((skill) => (
                    <span key={skill} className="px-2 py-1 bg-secondary text-xs rounded border border-border">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Globe className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-bold text-primary">Languages</h3>
                </div>
                <div className="space-y-1 text-sm text-foreground/80">
                  <p className="flex justify-between"><span>English</span> <span className="text-accent">Native/Fluent</span></p>
                  <p className="flex justify-between"><span>Malayalam</span> <span className="text-accent">Native/Fluent</span></p>
                  <p className="flex justify-between"><span>Arabic</span> <span className="text-muted-foreground">Basic</span></p>
                  <p className="flex justify-between"><span>Tamil</span> <span className="text-muted-foreground">Basic</span></p>
                  <p className="flex justify-between"><span>Hindi</span> <span className="text-muted-foreground">Basic</span></p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-bold text-primary mb-4">Leadership & Clubs</h3>
                <ul className="text-xs space-y-2 text-muted-foreground">
                  <li>• President, Radio Club SISJ</li>
                  <li>• Jt. Secretary, Social Awareness Club</li>
                  <li>• Head of Media, Cyber Squad (CSMS)</li>
                  <li>• Assistant Event Organizer, Ebae Club</li>
                  <li>• Lead Sound Advisor, Music Club</li>
                  <li>• Technical Lead, SIS Fest'24</li>
                </ul>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>
    </div>
  );
};

export default ResumeWindow;